import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { authHeaders, Toast } from './listPageStyles';
import { StatusBadge } from './ui';
import { usePageMeta } from '../hooks/usePageMeta';
import {
  DetailShell, Section, Grid, Icon, SubModal, SplitButton,
  FieldText, FieldSelect, FieldDate, FieldTextarea,
} from './detail/DetailShell';
import SurveyFlowEditor from './SurveyFlowEditor';
import SurveyQuestionsDesigner from './SurveyQuestionsDesigner';
import SurveyNotificationsTab from './SurveyNotificationsTab';
import { MultiChipSelect } from './campaign/MultiChipSelect';
import { RELATION_CONFIGS } from './relationConfigs';
import { usePageActions } from '../contexts/PermissionContext';

const API_BASE = '/api/surveys';

// Anket Uygulama Alanı — anketin kime uygulanacağı. Seçim, hedef kitle kriterlerini belirler:
//   Müşteri Anketi  → Müşteri kriterleri (Müşteri Segmenti, Bölge)
//   Personel Anketi → Personel kriterleri (Temsilci, Temsilci Grubu)
const TARGET_TYPES = [
  { value: 'Customer', label: 'Müşteri Anketi' },
  { value: 'Representative', label: 'Personel Anketi' },
];

// Anket Form Tipi — soruların katılımcıya sunum biçimi.
//   FORM        → tüm sorular tek formda birlikte gösterilir.
//   SEQUENTIAL  → "Sıralı Getir": sorular tek tek, sırayla getirilir.
const FORM_TYPES = [
  { value: 'FORM', label: 'Form' },
  { value: 'SEQUENTIAL', label: 'Sıralı Getir' },
];

// Uygulama alanına göre geçerli hedef kitle kriterleri. Alan değişince diğer alanın
// kriterleri temizlenir (görünmeyen ama kayıtlı kalan kriter, yayın kontrolünü kilitler).
const DOMAIN_CRITERIA = {
  Customer: ['customers', 'region'],
  Representative: ['people', 'repGroup'],
};

// A1 — Yaşam döngüsü durumu. "SüresiDoldu" sistem tarafından (bitiş tarihi geçince)
// otomatik atanır; yüklenen değer buysa gösterilir ama elle seçilebilir bırakılmaz.
const STATUS_OPTIONS = [
  { value: 'Taslak', label: 'Taslak' },
  { value: 'Yayında', label: 'Yayında (mobilde görünür)' },
  { value: 'Arşivde', label: 'Arşivde' },
];
const STATUS_OPTIONS_WITH_EXPIRED = [
  ...STATUS_OPTIONS,
  { value: 'SüresiDoldu', label: 'Süresi Doldu' },
];

const EMPTY_FORM = {
  textId: '', title: '', description: '', status: 'Taslak', surveyType: '',
  startDate: '', endDate: '', targetType: 'Customer', formType: 'FORM',
  // Ayarlar (Epic F)
  isMandatory: false, audienceScope: 'all', audienceCriteria: [],
  audiencePeople: [], audienceSegments: [], audienceRegions: [], audienceRepGroups: [],
  blockRepeat: false, showProgress: true,
  // Bildirimler (Bölüm 4.5)
  notifications: { onCompleteEnabled: false, onCompleteRecipients: [], conditionalRules: [] },
};

function parseJsonArray(v) {
  if (!v) return [];
  if (Array.isArray(v)) return v;
  try { const a = JSON.parse(v); return Array.isArray(a) ? a : []; } catch { return []; }
}

// Hedef kitle seçimlerini MultiChipSelect'in beklediği {value,label} dizisine çevirir.
// Eski kayıtlar düz string dizisi olabilir (o zaman label = value).
function toChipItems(arr) {
  return (arr || []).map((x) => (typeof x === 'string' ? { value: x, label: x } : { value: x.value ?? x.Value, label: x.label ?? x.Label ?? x.value ?? x.Value }));
}

// Ayarlar sekmesi için küçük UI yardımcıları
function SwitchRow({ label, desc, checked, onChange }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '6px 0' }}>
      <div>
        <div style={{ fontSize: 13.5, fontWeight: 600, color: '#0f172a' }}>{label}</div>
        {desc && <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{desc}</div>}
      </div>
      <button type="button" onClick={() => onChange(!checked)}
        style={{ width: 40, height: 23, borderRadius: 999, border: 'none', cursor: 'pointer', background: checked ? '#4F46E5' : '#cbd5e1', position: 'relative', flexShrink: 0 }}>
        <span style={{ position: 'absolute', top: 2, left: checked ? 19 : 2, width: 19, height: 19, borderRadius: '50%', background: '#fff', transition: 'left .15s' }} />
      </button>
    </div>
  );
}


function parseNotifications(v) {
  const base = { onCompleteEnabled: false, onCompleteRecipients: [], conditionalRules: [] };
  if (!v) return base;
  try {
    const o = typeof v === 'string' ? JSON.parse(v) : v;
    return {
      onCompleteEnabled: !!o.onCompleteEnabled,
      onCompleteRecipients: Array.isArray(o.onCompleteRecipients) ? o.onCompleteRecipients : [],
      conditionalRules: Array.isArray(o.conditionalRules) ? o.conditionalRules : [],
    };
  } catch { return base; }
}


// Ayar alanlarını backend kaydından form'a yükler.
function surveySettingsFromRow(r) {
  return {
    isMandatory: !!pick(r, 'isMandatory', 'IsMandatory'),
    audienceScope: pick(r, 'audienceScope', 'AudienceScope') || 'all',
    audienceCriteria: (() => {
      const c = parseJsonArray(pick(r, 'audienceCriteriaJson', 'AudienceCriteriaJson'));
      if (c.length) return c;
      // Geriye uyum: eski tek-kapsam → kriter listesine çevir.
      const scope = pick(r, 'audienceScope', 'AudienceScope');
      return scope && scope !== 'all' ? [scope] : [];
    })(),
    audiencePeople: toChipItems(parseJsonArray(pick(r, 'audiencePeopleJson', 'AudiencePeopleJson'))),
    audienceSegments: toChipItems(parseJsonArray(pick(r, 'audienceSegmentsJson', 'AudienceSegmentsJson'))),
    audienceRegions: toChipItems(parseJsonArray(pick(r, 'audienceRegionsJson', 'AudienceRegionsJson'))),
    audienceRepGroups: toChipItems(parseJsonArray(pick(r, 'audienceRepGroupsJson', 'AudienceRepGroupsJson'))),
    blockRepeat: !!pick(r, 'blockRepeat', 'BlockRepeat'),
    showProgress: pick(r, 'showProgressBar', 'ShowProgressBar') ?? true,
    notifications: parseNotifications(pick(r, 'notificationsJson', 'NotificationsJson')),
  };
}


function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  return undefined;
}
function unwrap(payload) { return pick(payload, 'Data', 'data') ?? payload; }
function toDateInput(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  } catch { return ''; }
}
function fromDateInput(value) {
  if (!value) return null;
  try { return new Date(`${value}T00:00:00`).toISOString(); } catch { return null; }
}
function fmtDateTime(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return '—'; }
}

// SurveyAnswer'ın metin gösterimi (foto ayrı render edilir).
function answerDisplayText(a) {
  const json = pick(a, 'answerJson', 'AnswerJson');
  if (json) {
    try {
      const arr = JSON.parse(json);
      if (Array.isArray(arr)) return arr.join(', ');
    } catch { /* düz metne düş */ }
    return String(json);
  }
  const numeric = pick(a, 'answerNumeric', 'AnswerNumeric');
  if (numeric !== undefined && numeric !== null) return String(numeric);
  const text = pick(a, 'answerText', 'AnswerText');
  if (text) return text;
  return '';
}

export function SurveyDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !id || id === 'new';
  const { canAdd, canEdit: canEditAction, canDelete } = usePageActions();
  const canSave = isNew ? canAdd : canEditAction;
  usePageMeta(isNew ? 'Yeni Anket' : 'Anket Detayı', 'Saha temsilcisine atanan anket, soruları ve cevapları.');

  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [tried, setTried] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [toast, setToast] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [audit, setAudit] = useState(null);

  // Cevaplar (salt-okunur drill-down)
  const [answers, setAnswers] = useState([]);
  const [answersLoading, setAnswersLoading] = useState(false);
  const [answersStats, setAnswersStats] = useState(null);
  const [validation, setValidation] = useState(null);
  const [validating, setValidating] = useState(false);
  const [canEdit, setCanEdit] = useState(true); // #7 Düzenleyici/Görüntüleyici
  // Bölge seçenekleri (Müşteri.Region) — MultiChipSelect fetchOptions kontratı (query, skip → {value,label}[]).
  // Kişiler ve Segment kendi RELATION_CONFIG'leriyle (/api/representatives, /api/customergroups) beslenir.
  const fetchRegions = useCallback(async (query = '', skip = 0) => {
    try {
      const res = await fetch(`${API_BASE}/source-lists?type=region`, { headers: authHeaders() });
      const b = unwrap(await res.json());
      const all = (pick(b, 'items', 'Items') || []).map((x) => ({ value: pick(x, 'name', 'Name'), label: pick(x, 'name', 'Name') }));
      const term = (query || '').toLocaleLowerCase('tr');
      const filtered = term ? all.filter((o) => (o.label || '').toLocaleLowerCase('tr').includes(term)) : all;
      return filtered.slice(skip, skip + 20);
    } catch { return []; }
  }, []);

  // Mevcut kullanıcının anket düzenleme yetkisi (Görüntüleyici → salt-okunur).
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/access`, { headers: authHeaders() });
        const b = unwrap(await res.json());
        setCanEdit(pick(b, 'canEdit', 'CanEdit') ?? true);
      } catch { /* sessiz — varsayılan düzenleyici */ }
    })();
  }, []);
  const readOnly = !canEdit;

  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 3500); };
  const set = (k, v) => setForm((c) => ({ ...c, [k]: v }));

  const load = useCallback(async () => {
    if (isNew) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/${id}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Anket alınamadı (${res.status})`);
      const r = unwrap(await res.json());
      setForm({
        textId: pick(r, 'textId', 'TextId') ?? '',
        title: pick(r, 'title', 'Title') ?? '',
        description: pick(r, 'description', 'Description') ?? '',
        status: pick(r, 'status', 'Status') || ((pick(r, 'isActive', 'IsActive') ?? true) ? 'Yayında' : 'Taslak'),
        surveyType: pick(r, 'surveyType', 'SurveyType') ?? '',
        startDate: toDateInput(pick(r, 'startDate', 'StartDate')),
        endDate: toDateInput(pick(r, 'endDate', 'EndDate')),
        targetType: pick(r, 'targetType', 'TargetType') ?? 'Customer',
        formType: pick(r, 'formType', 'FormType') || 'FORM',
        ...surveySettingsFromRow(r),
      });
      setAudit({ createdBy: pick(r, 'createdBy', 'CreatedBy', 'createdUser', 'CreatedUser'), createdDate: pick(r, 'createdDate', 'CreatedDate'), updatedBy: pick(r, 'updatedBy', 'UpdatedBy', 'modifiedUser', 'ModifiedUser'), updatedDate: pick(r, 'updatedDate', 'UpdatedDate', 'modifiedDate', 'ModifiedDate') });
    } catch (err) {
      showToast('error', err.message || 'Anket alınamadı');
    } finally {
      setLoading(false);
    }
  }, [id, isNew]);

  // Tek kayıt yükleme: önce location.state.row (listeden), yoksa GetById.
  const hydrateFromRow = useCallback((r) => {
    setForm({
      textId: pick(r, 'textId', 'TextId') ?? '',
      title: pick(r, 'title', 'Title') ?? '',
      description: pick(r, 'description', 'Description') ?? '',
      status: pick(r, 'status', 'Status') || ((pick(r, 'isActive', 'IsActive') ?? true) ? 'Yayında' : 'Taslak'),
      surveyType: pick(r, 'surveyType', 'SurveyType') ?? '',
      startDate: toDateInput(pick(r, 'startDate', 'StartDate')),
      endDate: toDateInput(pick(r, 'endDate', 'EndDate')),
      targetType: pick(r, 'targetType', 'TargetType') ?? 'Customer',
      ...surveySettingsFromRow(r),
    });
    // Nav-state satırı liste OData $select'inden gelir → audit alanları eksik.
    // Formu anında doldur; audit'i tam kayıttan (GetById) NON-BLOCKING çek.
    fetch(`${API_BASE}/${id}`, { headers: authHeaders() })
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => { const rec = unwrap(body); if (rec) setAudit({ createdBy: pick(rec, 'createdBy', 'CreatedBy', 'createdUser', 'CreatedUser'), createdDate: pick(rec, 'createdDate', 'CreatedDate'), updatedBy: pick(rec, 'updatedBy', 'UpdatedBy', 'modifiedUser', 'ModifiedUser'), updatedDate: pick(rec, 'updatedDate', 'UpdatedDate', 'modifiedDate', 'ModifiedDate') }); })
      .catch(() => {});
  }, [id]);

  useEffect(() => {
    if (isNew) { setLoading(false); return; }
    if (location.state?.row) { hydrateFromRow(location.state.row); setLoading(false); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isNew]);

  // Klonla: /surveys/new'e kopya veriyle gelindiyse formu doldur (kayıt OLUŞTURMA — kullanıcı Kaydet diyene kadar).
  useEffect(() => {
    if (isNew && location.state?.clone) {
      setForm(location.state.clone);
      setTried(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // Uygulama alanı (targetType) değişince, diğer alana ait işaretli kriterleri ayıkla.
  // Yükleme sırasında kriterler zaten alanla tutarlı olduğundan no-op'tur.
  useEffect(() => {
    const valid = DOMAIN_CRITERIA[form.targetType] || [];
    setForm((c) => {
      const cur = c.audienceCriteria || [];
      const pruned = cur.filter((k) => valid.includes(k));
      return pruned.length === cur.length ? c : { ...c, audienceCriteria: pruned };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.targetType]);

  // ── Cevaplar (salt-okunur) ──
  const fetchAnswers = useCallback(async (surveyId) => {
    if (!surveyId) return;
    setAnswersLoading(true);
    setAnswers([]);
    setAnswersStats(null);
    try {
      const res = await fetch(`${API_BASE}/${surveyId}/answers?take=1000`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Cevaplar alınamadı (${res.status})`);
      const data = unwrap(await res.json());
      setAnswers(pick(data, 'items', 'Items') ?? []);
      setAnswersStats({
        total: pick(data, 'total', 'Total') ?? 0,
        respondentCount: pick(data, 'respondentCount', 'RespondentCount') ?? 0,
        customerCount: pick(data, 'customerCount', 'CustomerCount') ?? 0,
        questionCount: pick(data, 'questionCount', 'QuestionCount') ?? 0,
      });
    } catch (err) {
      showToast('error', err.message || 'Cevaplar alınamadı');
      setAnswers([]);
    } finally {
      setAnswersLoading(false);
    }
  }, []);

  useEffect(() => { if (!isNew && activeTab === 'answers') fetchAnswers(id); }, [isNew, activeTab, id, fetchAnswers]);

  const buildBody = () => ({
    textId: form.textId.trim(),
    title: form.title.trim(),
    description: form.description.trim() || null,
    status: form.status,
    surveyType: form.surveyType.trim() || null,
    isActive: form.status === 'Yayında',
    startDate: fromDateInput(form.startDate),
    endDate: fromDateInput(form.endDate),
    targetType: form.targetType,
    formType: form.formType || 'FORM',
    // Ayarlar (Epic F)
    isMandatory: !!form.isMandatory,
    audienceScope: (form.audienceCriteria || []).length === 1 ? form.audienceCriteria[0] : 'all',
    audienceCriteriaJson: JSON.stringify(form.audienceCriteria || []),
    audiencePeopleJson: JSON.stringify(form.audiencePeople || []),
    audienceSegmentsJson: JSON.stringify(form.audienceSegments || []),
    audienceRegionsJson: JSON.stringify(form.audienceRegions || []),
    audienceRepGroupsJson: JSON.stringify(form.audienceRepGroups || []),
    blockRepeat: !!form.blockRepeat,
    showProgressBar: !!form.showProgress,
    notificationsJson: JSON.stringify(form.notifications || { onCompleteEnabled: false, onCompleteRecipients: [], conditionalRules: [] }),
  });

  // andNew: kaydet ama listeye/detaya gitme — formu sıfırlayıp boş yeni kayda hazırlan.
  const persist = async ({ andNew = false } = {}) => {
    setTried(true);
    if (!form.textId.trim()) { showToast('error', 'TextId zorunlu.'); return; }
    if (!form.title.trim()) { showToast('error', 'Başlık zorunlu.'); return; }
    setSaving(true);
    try {
      const res = await fetch(isNew ? API_BASE : `${API_BASE}/${id}`, {
        method: isNew ? 'POST' : 'PUT', headers: authHeaders(), body: JSON.stringify(buildBody()),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'Message', 'message') || `Kayıt başarısız (${res.status})`);
      if (isNew) {
        if (andNew) {
          showToast('success', 'Anket oluşturuldu. Yeni kayda devam edebilirsiniz.');
          setForm(EMPTY_FORM);
          setTried(false);
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
        showToast('success', 'Anket oluşturuldu.');
        setTimeout(() => navigate('/surveys'), 400);
        return;
      }
      showToast('success', 'Anket güncellendi.');
      setTimeout(() => navigate('/surveys'), 400); // kaydedince listeye dön
    } catch (err) {
      showToast('error', err.message || 'Kayıt başarısız');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => persist();

  // Epic H — yayın kontrolü (hata/uyarı önizleme)
  const runValidation = async () => {
    if (isNew) { showToast('error', 'Önce anketi kaydedin.'); return; }
    setValidating(true);
    try {
      const res = await fetch(`${API_BASE}/${id}/validate`, { headers: authHeaders() });
      const body = unwrap(await res.json());
      setValidation({
        ok: pick(body, 'ok', 'Ok') ?? false,
        errors: pick(body, 'errors', 'Errors') ?? [],
        warnings: pick(body, 'warnings', 'Warnings') ?? [],
      });
    } catch (err) {
      showToast('error', err.message || 'Doğrulama başarısız');
    } finally {
      setValidating(false);
    }
  };

  // Klonla: mevcut kaydın form bilgilerini taşıyarak yeni-kayıt ekranını aç (TextId hariç — benzersiz olmalı).
  const handleClone = () => {
    navigate('/surveys/new', { state: { clone: { ...form, textId: '' } } });
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/${id}`, { method: 'DELETE', headers: authHeaders() });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'Message', 'message') || `Silme başarısız (${res.status})`);
      showToast('success', 'Anket silindi (sorular cascade ile silindi).');
      setTimeout(() => navigate('/surveys'), 400);
    } catch (err) {
      showToast('error', err.message || 'Silme başarısız');
      setSaving(false);
      setDeleteConfirm(false);
    }
  };

  // ── Soru modal (alt-kayıt — kendi alt-endpoint'ine anında kaydeder) ──
  const title = isNew ? 'Yeni Anket' : (form.title || 'Anket Detayı');
  const STATUS_TONES = { Yayında: 'ok', Taslak: 'neutral', SüresiDoldu: 'warn', Arşivde: 'neutral' };
  const STATUS_LABELS = { Yayında: 'Yayında', Taslak: 'Taslak', SüresiDoldu: 'Süresi Doldu', Arşivde: 'Arşivde' };
  const statusBadge = !isNew && (
    <StatusBadge tone={STATUS_TONES[form.status] || 'neutral'}>
      {STATUS_LABELS[form.status] || form.status}
    </StatusBadge>
  );

  const tabs = [
    { key: 'general', label: 'Genel', icon: 'list' },
    { key: 'questions', label: 'Sorular', icon: 'layers', disabled: isNew, title: isNew ? 'Önce anketi kaydedin' : undefined },
    { key: 'flow', label: 'Akış', icon: 'git-branch', disabled: isNew, title: isNew ? 'Önce anketi kaydedin' : undefined },
    { key: 'notifications', label: 'Bildirimler', icon: 'check', disabled: isNew, title: isNew ? 'Önce anketi kaydedin' : undefined },
    { key: 'settings', label: 'Ayarlar', icon: 'info' },
    { key: 'answers', label: 'Cevaplar', icon: 'users', disabled: isNew, title: isNew ? 'Önce anketi kaydedin' : undefined },
  ];

  const actions = readOnly ? (
    <>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 600, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '6px 12px' }}>
        <Icon name="info" size={14} /> Salt okunur (Görüntüleyici)
      </span>
      <button className="dps-btn ghost" onClick={() => navigate('/surveys')}>Kapat</button>
    </>
  ) : (
    <>
      {!isNew && canDelete && <button className="dps-btn danger" onClick={() => setDeleteConfirm(true)} disabled={saving}><Icon name="x" size={16} /> Sil</button>}
      <button className="dps-btn ghost" onClick={() => navigate('/surveys')} disabled={saving}>İptal</button>
      {canSave && (isNew ? (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={handleSave}
          disabled={saving}
          menu={[{
            label: 'Kaydet ve Yeni',
            desc: 'Kaydet, listeye dönme, yeni kayıt aç',
            icon: 'plus',
            onClick: () => persist({ andNew: true }),
          }]}
        />
      ) : (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={handleSave}
          disabled={saving}
          menu={[{
            label: 'Klonla',
            desc: 'Bu kaydı kopyalayıp yeni anket aç',
            icon: 'copy',
            onClick: handleClone,
          }]}
        />
      ))}
    </>
  );


  return (
    <DetailShell
      crumb={[{ label: 'Anketler', to: '/surveys' }, { label: isNew ? 'Yeni Anket' : 'Anket Detayı', current: true }]}
      status={statusBadge}
      tabs={tabs}
      activeTab={activeTab}
      onTab={setActiveTab}
      actions={actions}
      audit={isNew ? null : audit}
      history={isNew ? null : { entityName: 'Survey', entityId: id }}
      loading={loading}
    >
      {activeTab === 'general' && (
        <div className="dps-form">
          <Section icon="list" title="Anket Bilgileri" subtitle="Kod, başlık, uygulama alanı ve yayın dönemi">
            <Grid cols={2}>
              <FieldText form={form} set={set} tried={tried} k="textId" label="TextId (Kod)" req mono placeholder="örn. tr-satisfaction-2026" />
              <FieldSelect form={form} set={set} k="targetType" label="Anket Uygulama Alanı" options={TARGET_TYPES} />
              <FieldText form={form} set={set} tried={tried} k="title" label="Başlık" req span />
              <FieldText form={form} set={set} k="surveyType" label="Anket Tipi" placeholder="örn. Memnuniyet, Denetim" />
              <FieldSelect form={form} set={set} k="status" label="Durum" options={form.status === 'SüresiDoldu' ? STATUS_OPTIONS_WITH_EXPIRED : STATUS_OPTIONS} />
              <FieldDate form={form} set={set} k="startDate" label="Başlangıç" />
              <FieldDate form={form} set={set} k="endDate" label="Bitiş" />
            </Grid>

            {/* Ayraç — Anket Form Tipi ayrı bir grup olarak ayrılır */}
            <div style={{ borderTop: '1px solid #e5e7eb', margin: '16px 0 14px' }} />
            <Grid cols={2}>
              <FieldSelect form={form} set={set} k="formType" label="Anket Form Tipi" options={FORM_TYPES} />
            </Grid>
          </Section>

          <Section icon="info" title="Açıklama" subtitle="Anketin amacı ve içeriği hakkında not">
            <Grid cols={2}>
              <FieldTextarea form={form} set={set} k="description" label="Açıklama" rows={3} span />
            </Grid>
          </Section>

          {!isNew && (
            <Section icon="check" title="Yayın Kontrolü" subtitle="Anketi yayına almadan önce hata/uyarıları denetleyin"
              right={<button className="dps-blk-add" onClick={runValidation} disabled={validating}><Icon name="check" size={15} /> {validating ? 'Kontrol ediliyor…' : 'Kontrol Et'}</button>}
            >
              {!validation ? (
                <div style={{ fontSize: 13, color: '#64748b' }}>Henüz kontrol yapılmadı. "Kontrol Et" ile kapsamlı yayın denetimini çalıştırın.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {validation.ok && validation.errors.length === 0 && (
                    <div style={{ fontSize: 13, color: '#0f7a57', background: '#e3f7ee', border: '1px solid #b6e8d4', borderRadius: 8, padding: '8px 12px', fontWeight: 600 }}>✓ Yayına alınabilir{validation.warnings.length ? ` (${validation.warnings.length} uyarı var)` : ''}.</div>
                  )}
                  {validation.errors.map((e, i) => (
                    <div key={`e${i}`} style={{ fontSize: 13, color: '#b91c1c', background: '#fee2e2', border: '1px solid #fecaca', borderRadius: 8, padding: '7px 12px' }}>⛔ {e}</div>
                  ))}
                  {validation.warnings.map((w, i) => (
                    <div key={`w${i}`} style={{ fontSize: 13, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '7px 12px' }}>⚠ {w}</div>
                  ))}
                </div>
              )}
            </Section>
          )}
        </div>
      )}


      {activeTab === 'questions' && !isNew && (
        <SurveyQuestionsDesigner surveyId={id} showToast={showToast} readOnly={readOnly} />
      )}

      {activeTab === 'flow' && !isNew && (
        <SurveyFlowEditor surveyId={id} showToast={showToast} readOnly={readOnly} />
      )}

      {activeTab === 'notifications' && !isNew && (
        <SurveyNotificationsTab
          surveyId={id}
          value={form.notifications}
          onChange={(n) => set('notifications', n)}
          showToast={showToast}
        />
      )}

      {activeTab === 'settings' && (
        <div className="dps-form">
          <Section icon="info" title="Anketin zorunluluğu" subtitle="Bu anket cevaplanmadan ilerlenip ilerlenemeyeceği">
            <SwitchRow label="Anket zorunlu olsun" desc="Açıkken katılımcı anketi tamamlamadan ilgili akışta ilerleyemez."
              checked={form.isMandatory} onChange={(v) => set('isMandatory', v)} />
          </Section>

          <Section icon="list" title="Geçerlilik tarihi" subtitle="Anketin aktif gösterileceği tarih aralığı">
            <Grid cols={2}>
              <FieldDate form={form} set={set} k="startDate" label="Başlangıç" />
              <FieldDate form={form} set={set} k="endDate" label="Bitiş" />
            </Grid>
          </Section>

          <Section icon="users" title="Hedef kitle"
            subtitle={`${form.targetType === 'Representative' ? 'Personel' : 'Müşteri'} kriterleri — birden fazla kriter işaretlenebilir; kriterler VE, kriter içi seçimler VEYA mantığıyla birleşir`}>
            {(() => {
              const isStaff = form.targetType === 'Representative';
              const crit = form.audienceCriteria || [];
              const has = (k) => crit.includes(k);
              const toggle = (k) => set('audienceCriteria', has(k) ? crit.filter((x) => x !== k) : [...crit, k]);
              return (
                <>
                  {crit.length === 0 && (
                    <div style={{ fontSize: 12.5, color: '#64748b', marginBottom: 8 }}>Hiçbir kriter seçili değil — anket <b>{isStaff ? 'tüm personele' : 'tüm müşterilere'}</b> gösterilir.</div>
                  )}
                  {isStaff ? (
                    <>
                      <SwitchRow label="Temsilci" desc="Satış temsilcilerinden çoklu seçim" checked={has('people')} onChange={() => toggle('people')} />
                      {has('people') && (
                        <div style={{ margin: '4px 0 10px' }}>
                          <MultiChipSelect items={form.audiencePeople} onChange={(v) => set('audiencePeople', v)} config={RELATION_CONFIGS.representative} placeholder="Temsilci ara ve ekle…" disabled={readOnly} />
                        </div>
                      )}
                      <SwitchRow label="Temsilci Grubu" desc="Temsilci gruplarından çoklu seçim" checked={has('repGroup')} onChange={() => toggle('repGroup')} />
                      {has('repGroup') && (
                        <div style={{ margin: '4px 0 2px' }}>
                          <MultiChipSelect items={form.audienceRepGroups} onChange={(v) => set('audienceRepGroups', v)} config={RELATION_CONFIGS.representativeGroup} placeholder="Temsilci grubu ara ve ekle…" disabled={readOnly} />
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <SwitchRow label="Müşteri Segmenti" desc="Müşteri gruplarından çoklu seçim" checked={has('customers')} onChange={() => toggle('customers')} />
                      {has('customers') && (
                        <div style={{ margin: '4px 0 10px' }}>
                          <MultiChipSelect items={form.audienceSegments} onChange={(v) => set('audienceSegments', v)} config={RELATION_CONFIGS.customerGroup} placeholder="Müşteri grubu ara ve ekle…" disabled={readOnly} />
                        </div>
                      )}
                      <SwitchRow label="Bölge" desc="Önceden tanımlı bölge listesinden çoklu seçim" checked={has('region')} onChange={() => toggle('region')} />
                      {has('region') && (
                        <div style={{ margin: '4px 0 2px' }}>
                          <MultiChipSelect items={form.audienceRegions} onChange={(v) => set('audienceRegions', v)} fetchOptions={fetchRegions} placeholder="Bölge ara ve ekle…" disabled={readOnly} />
                        </div>
                      )}
                    </>
                  )}
                </>
              );
            })()}
          </Section>

          <Section icon="info" title="Diğer ayarlar" subtitle="Yanıt davranışına ilişkin ek seçenekler">
            <SwitchRow label="Tekrar doldurmaya kapalı" desc="Açıkken aynı katılımcı anketi bir defadan fazla dolduramaz."
              checked={form.blockRepeat} onChange={(v) => set('blockRepeat', v)} />
            <SwitchRow label="İlerleme çubuğu göster" desc="Katılımcı ekranında tamamlanma göstergesi görünür."
              checked={form.showProgress} onChange={(v) => set('showProgress', v)} />
          </Section>
        </div>
      )}

      {activeTab === 'answers' && !isNew && (
        <div className="dps-form">
          <Section
            icon="users"
            title="Cevaplar"
            subtitle="Sahadan toplanan cevaplar (salt-okunur)"
          >
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
              <StatusBadge tone="ok">Cevap: {answersStats?.total ?? 0}</StatusBadge>
              <StatusBadge tone="ok">Temsilci: {answersStats?.respondentCount ?? 0}</StatusBadge>
              <StatusBadge tone="wait">Müşteri: {answersStats?.customerCount ?? 0}</StatusBadge>
              <StatusBadge tone="neutral">Soru: {answersStats?.questionCount ?? 0}</StatusBadge>
            </div>
            {answersLoading ? (
              <div className="dps-empty"><span>Yükleniyor…</span></div>
            ) : answers.length === 0 ? (
              <div className="dps-empty"><Icon name="users" size={34} /><p>Henüz cevap yok</p><span>Cevaplar mobil senkronizasyon (push) ile gelir.</span></div>
            ) : (
              <table className="dps-table">
                <thead>
                  <tr>
                    <th>Soru</th><th>Cevap</th><th>Müşteri</th><th>Temsilci</th><th>Tarih</th>
                  </tr>
                </thead>
                <tbody>
                  {answers.map((a, idx) => {
                    const photo = pick(a, 'photoUrl', 'PhotoUrl');
                    const text = answerDisplayText(a);
                    return (
                      <tr key={pick(a, 'id', 'Id') || idx}>
                        <td>
                          <strong>{pick(a, 'questionText', 'QuestionText') || '—'}</strong>
                          {pick(a, 'questionType', 'QuestionType') && (
                            <div style={{ fontSize: 11, color: '#94a3b8' }}>{pick(a, 'questionType', 'QuestionType')}</div>
                          )}
                        </td>
                        <td>
                          {photo ? (
                            <a href={photo} target="_blank" rel="noreferrer"
                               style={{ display: 'inline-block', width: 56, height: 56, borderRadius: 8, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
                              <img src={photo} alt="Cevap" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                   onError={(e) => { e.target.style.display = 'none'; e.target.parentNode.style.background = '#f3f4f6'; }} />
                            </a>
                          ) : (
                            <strong>{text || '—'}</strong>
                          )}
                        </td>
                        <td>{pick(a, 'customerTitle', 'CustomerTitle') || '—'}</td>
                        <td>{pick(a, 'representativeName', 'RepresentativeName') || '—'}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(pick(a, 'answeredAt', 'AnsweredAt'))}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </Section>
        </div>
      )}


      {/* Anket silme onayı */}
      {deleteConfirm && (
        <SubModal
          title="Silme Onayı"
          maxWidth={440}
          onClose={() => setDeleteConfirm(false)}
          footer={(
            <>
              <button className="dps-btn ghost" onClick={() => setDeleteConfirm(false)} disabled={saving}>İptal</button>
              <button className="dps-btn danger" onClick={handleDelete} disabled={saving}>{saving ? 'Siliniyor…' : 'Evet, Sil'}</button>
            </>
          )}
        >
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            <strong>“{title}”</strong> anketini silmek istediğinize emin misiniz?
          </p>
          <div style={{ fontSize: 12, color: '#dc2626', marginTop: 8 }}>
            Anket ve tüm soruları (cascade) silinir. Mevcut cevaplar tabloda dangling kalabilir.
          </div>
        </SubModal>
      )}

      <Toast toast={toast} />
    </DetailShell>
  );
}

export default SurveyDetailPage;
